import { useCallback, useMemo } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import bakedPayload from "@/data/emailResponseLoop.json";
import { Card } from "@/design/primitives";
import { ResponseLoopBoard } from "./ResponseLoopBoard";

// The response-loop board at /email/loop — the email coaching loop's own
// route, mounted beside the mail client (/email) and the zero-inbox wall
// (/email/board). It answers three questions in one screen: what went out,
// what Kit learned from your notes, and what is waiting on you.
//
// The data is the redacted board payload the ingest writes (the same file
// the differential gate verifies). The full-text sent log stays local.
//
// The note affordance rides the board's existing note verb — the same verb
// every other board uses — so a note on a sent email lands in the record
// and the coach queue picks it up on the next ingest cycle.

export default function ResponseLoopPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  // The live board carries the payload's rows when the backend has them;
  // the committed payload is the fallback so the page never renders empty.
  const board = useBoard("email");

  const payload = useMemo(() => {
    const live = board.items || [];
    if (!live.length) return bakedPayload;
    // The live board rows carry the payload under .payload (the ingest
    // shape). The first row that carries a board payload wins.
    const withPayload = live.find((i) => i.payload && i.payload.schema_version);
    if (withPayload) return withPayload.payload;
    return bakedPayload;
  }, [board.items]);

  // The note verb. A note on a sent row rides the standard note action, so
  // the record keeps it and the coach queue picks it up.
  const onNote = useCallback(
    async (row, text) => {
      const target = row.message_token || row.slug;
      const res = await board.act("note", target, text);
      return Boolean(res);
    },
    [board],
  );

  return (
    <div data-testid="response-loop-page" className="w-full font-inter text-os-ink">
      <div
        className={
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-5 px-10 py-6"
            : "flex flex-col gap-4 px-4 pb-8 pt-4"
        }
      >
        {board.status === "down" && !board.live && (
          <Card quiet className="border-os-red px-5 py-3" data-testid="response-loop-unreadable">
            <p className="text-[0.875rem] leading-[1.45] text-os-red">
              Kit can&apos;t reach the live board right now — this is the last committed state.
            </p>
          </Card>
        )}
        <ResponseLoopBoard payload={payload} onNote={onNote} />
      </div>
    </div>
  );
}
